'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import CategoryFilter from './CategoryFilter';
import FilterControls from './FilterControls';
import MenuGrid from './MenuGrid';
import CartSummary from '@/components/common/CartSummary';
import { useCart } from '@/contexts/CartContext';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  alt: string;
  category: string;
  isVeg: boolean;
  popularity: number;
  isCurrentOffer?: boolean;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

const CATEGORY_ICONS: Record<string, string> = {
  all: 'Squares2X2Icon',
  starters: 'SparklesIcon',
  'main-course': 'FireIcon',
  combos: 'GiftIcon',
  desserts: 'CakeIcon',
  beverages: 'BeakerIcon',
  soup: 'CircleStackIcon',
  salad: 'RectangleGroupIcon',
};

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400';

const MenuCatalogInteractive = () => {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [cities, setCities] = useState<{ cityId: string; name: string; pin?: string }[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [dietaryFilter, setDietaryFilter] = useState<'all' | 'veg' | 'non-veg'>('all');
  const [currentOfferFilter, setCurrentOfferFilter] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<'popularity' | 'price-low' | 'price-high' | 'name'>(
    'popularity'
  );
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);

  const { cartItems, addToCart, updateQuantity, removeFromCart, orderCityId, setOrderCityId } = useCart();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (searchParams.get('currentOffer') === 'true') setCurrentOfferFilter(true);
  }, [searchParams]);

  // Restore city from cart context (e.g. returning user)
  useEffect(() => {
    if (orderCityId && selectedCity === '') setSelectedCity(orderCityId);
  }, [orderCityId]);

  // Sync selected city to cart context so checkout can send it with the order (Sales Analytics)
  useEffect(() => {
    if (selectedCity !== orderCityId) setOrderCityId(selectedCity);
  }, [selectedCity, orderCityId, setOrderCityId]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/cities')
      .then((r) => r.ok ? r.json() : [])
      .then((list: { cityId: string; name: string; pin?: string }[]) => {
        if (!cancelled) setCities(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (!cancelled) setCities([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchCategories() {
      try {
        const params = new URLSearchParams();
        if (selectedCity) params.set('city', selectedCity);
        if (dietaryFilter === 'veg') params.set('veg', 'true');
        if (dietaryFilter === 'non-veg') params.set('veg', 'false');
        if (currentOfferFilter) params.set('currentOffer', 'true');
        params.set('minPrice', String(priceRange[0]));
        params.set('maxPrice', String(priceRange[1]));
        if (searchQuery.trim()) params.set('search', searchQuery.trim());
        const res = await fetch(`/api/menu/categories?${params}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const list = data.categories ?? [];
        const totalCount = typeof data.totalCount === 'number' ? data.totalCount : list.reduce((s: number, c: { itemCount?: number }) => s + (c.itemCount ?? 0), 0);
        setCategories([
          { id: 'all', name: 'All Items', icon: 'Squares2X2Icon', count: totalCount },
          ...list.map((c: { slug: string; name: string; itemCount?: number }) => ({
            id: c.slug,
            name: c.name,
            icon: CATEGORY_ICONS[c.slug] || 'Squares2X2Icon',
            count: c.itemCount ?? 0,
          })),
        ]);
      } catch (_) {
        if (!cancelled) setCategories([{ id: 'all', name: 'All Items', icon: 'Squares2X2Icon', count: 0 }]);
      }
    }
    fetchCategories();
    return () => { cancelled = true; };
  }, [selectedCity, dietaryFilter, currentOfferFilter, searchQuery, priceRange]);

  useEffect(() => {
    let cancelled = false;
    setMenuLoading(true);
    async function fetchItems() {
      try {
        const params = new URLSearchParams();
        params.set('limit', '100');
        if (selectedCity) params.set('city', selectedCity);
        if (activeCategory && activeCategory !== 'all') params.set('category', activeCategory);
        if (dietaryFilter === 'veg') params.set('veg', 'true');
        if (dietaryFilter === 'non-veg') params.set('veg', 'false');
        if (currentOfferFilter) params.set('currentOffer', 'true');
        params.set('sort', sortBy);
        params.set('minPrice', String(priceRange[0]));
        params.set('maxPrice', String(priceRange[1]));
        if (searchQuery.trim()) params.set('search', searchQuery.trim());
        const res = await fetch(`/api/menu/items?${params}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const items = data.items ?? [];
        setMenuItems(items.map((i: { id: string; name: string; description: string; price: number; image: string | null; alt: string; category: string; isVeg: boolean; isPopular: boolean; isCurrentOffer?: boolean }) => ({
          id: i.id,
          name: i.name,
          description: i.description ?? '',
          price: Number(i.price),
          image: i.image || PLACEHOLDER_IMAGE,
          alt: i.alt || i.name,
          category: i.category,
          isVeg: i.isVeg,
          popularity: i.isPopular ? 4.5 : 0,
          isCurrentOffer: Boolean(i.isCurrentOffer),
        })));
      } catch (_) {
        if (!cancelled) setMenuItems([]);
      } finally {
        if (!cancelled) setMenuLoading(false);
      }
    }
    fetchItems();
    return () => { cancelled = true; };
  }, [selectedCity, activeCategory, dietaryFilter, currentOfferFilter, sortBy, priceRange, searchQuery]);

  const handleQuantityChange = (itemId: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [itemId]: quantity,
    }));
  };

  const handleAddToCart = (itemId: string) => {
    const item = menuItems.find((i) => i.id === itemId);
    const quantity = quantities[itemId] || 0;

    if (item && quantity > 0) {
      addToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: quantity,
        image: item.image,
      });
      setQuantities((prev) => ({
        ...prev,
        [itemId]: 0,
      }));
    }
  };

  const handleCartQuantityUpdate = (itemId: string, quantity: number) => {
    updateQuantity(itemId, quantity);
  };

  const handleRemoveFromCart = (itemId: string) => {
    removeFromCart(itemId);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="h-64 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row">
      <div className="flex-1 min-w-0">
        <div className="mx-auto max-w-7xl px-4 pt-4 pb-8 sm:px-6 lg:px-8">
          {/* Single filter at top – aligned with category sidebar on desktop (same width as sidebar) */}
          <div className="mb-4 flex flex-col lg:flex-row lg:gap-8">
            <div className="lg:w-64 lg:flex-shrink-0">
              <div className="relative">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={18}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter menu or search items..."
                  className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground placeholder-muted-foreground transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-40 focus:border-primary"
                  aria-label="Filter menu"
                />
              </div>
            </div>
            <div className="hidden lg:block flex-1" />
          </div>

          <div className="flex flex-col lg:flex-row lg:space-x-8">
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />

            <div className="flex-1 mt-4 lg:mt-0">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
                    {categories.find((cat) => cat.id === activeCategory)?.name || 'Menu'}
                  </h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {menuLoading
                      ? 'Loading…'
                      : (() => {
                          const total =
                            activeCategory === 'all'
                              ? (categories.find((c) => c.id === 'all')?.count ?? menuItems.length)
                              : (categories.find((c) => c.id === activeCategory)?.count ?? menuItems.length);
                          return `${total} delicious ${total === 1 ? 'item' : 'items'} available`;
                        })()}
                  </p>
                </div>
                {cities.length > 0 && (
                  <div className="min-w-[180px] shrink-0">
                    <label className="text-caption font-medium text-foreground mb-2 block">City</label>
                    <select
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      className="w-full rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground focus-ring hover:border-primary"
                    >
                      <option value="">All cities</option>
                      {cities.map((c) => (
                        <option key={c.cityId} value={c.cityId}>{c.name}{c.pin ? ` (${c.pin})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <FilterControls
                  dietaryFilter={dietaryFilter}
                  onDietaryFilterChange={setDietaryFilter}
                  currentOfferFilter={currentOfferFilter}
                  onCurrentOfferFilterChange={setCurrentOfferFilter}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  priceRange={priceRange}
                  onPriceRangeChange={setPriceRange}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </div>

              <MenuGrid
                items={menuItems}
                quantities={quantities}
                onQuantityChange={handleQuantityChange}
                onAddToCart={handleAddToCart}
                hasActiveFilters={!!selectedCity || dietaryFilter !== 'all' || currentOfferFilter || !!searchQuery.trim() || priceRange[0] > 0 || priceRange[1] < 1000}
              />
            </div>
          </div>
        </div>
      </div>

      <CartSummary className="" asDrawer />
    </div>
  );
};

export default MenuCatalogInteractive;
